import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Button,
  Group,
  MultiSelect,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput
} from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { useAppSelector } from '@/store/hooks';

import { UserRole } from '@/types/user';

import { usesMyTemplateRoutes } from '../physician/TransmissionReportPage/ruleBasedTemplates';

import {
  ALERT_TYPE_OPTIONS,
  APPOINTMENT_TYPE_OPTIONS,
  archiveInterpretationRule,
  ClinicUserOption,
  createInterpretationRule,
  DEVICE_TYPE_OPTIONS,
  InterpretationKind,
  InterpretationRuleRecord,
  InterpretationTemplateWrite,
  listClinicUsers,
  listInterpretationRules,
  reorderInterpretationRules,
  restoreInterpretationRule,
  updateInterpretationRule
} from './interpretationRulesApi';
import { letterClinics } from './letterTemplates';

interface TemplateDraft {
  name: string;
  body: string;
  isDefault: boolean;
}

interface RuleDraft {
  id?: string;
  isCatchAll: boolean;
  isArchived: boolean;
  deviceTypes: string[];
  alertTypes: string[];
  appointmentTypes: string[];
  templates: TemplateDraft[];
}

function toDraft(rule: InterpretationRuleRecord): RuleDraft {
  const templates = (rule.templates ?? []).map((template) => ({
    name: template.name,
    body: template.body,
    isDefault: template.isDefault
  }));
  return {
    id: rule.id,
    isCatchAll: rule.isCatchAll,
    isArchived: Boolean(rule.isArchived),
    deviceTypes: rule.deviceTypes ?? [],
    alertTypes: rule.alertTypes ?? [],
    appointmentTypes: rule.appointmentTypes ?? [],
    templates: templates.length ? templates : [{ name: '', body: '', isDefault: true }]
  };
}

function blankRule(): RuleDraft {
  return {
    isCatchAll: false,
    isArchived: false,
    deviceTypes: [],
    alertTypes: [],
    appointmentTypes: [],
    templates: [{ name: '', body: '', isDefault: true }]
  };
}

function withOneDefault(templates: TemplateDraft[], index: number): TemplateDraft[] {
  return templates.map((template, templateIndex) => ({
    ...template,
    isDefault: templateIndex === index
  }));
}

function ruleLabel(rule: RuleDraft): string {
  if (rule.isCatchAll) return 'Catch-all';
  const names = rule.templates.map((template) => template.name.trim()).filter(Boolean);
  return names.join(', ') || 'Untitled rule';
}

const InterpretationTemplatesSection = ({ mode }: { mode: 'mine' | 'clinic' }) => {
  const user = useAppSelector((state) => state.auth.user);
  const clinics = useMemo(() => letterClinics(user?.organizations), [user?.organizations]);
  const [clinicId, setClinicId] = useState(clinics[0]?.id ?? '');
  const [kind, setKind] = useState<InterpretationKind>(
    user?.role &&
      [
        UserRole.ExternalPractitioner,
        UserRole.ExternalPractitionerAdmin,
        UserRole.ExternalProvider
      ].includes(user.role)
      ? 'physician'
      : 'clinician'
  );
  const [stack, setStack] = useState<'clinic' | 'user'>('clinic');
  const [ownerUserId, setOwnerUserId] = useState<string | null>(null);
  const [users, setUsers] = useState<ClinicUserOption[]>([]);
  const [rules, setRules] = useState<RuleDraft[]>([]);
  const [editing, setEditing] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const useMineRoutes = mode === 'mine' && usesMyTemplateRoutes(user?.role);
  const ownerForAdmin =
    mode === 'mine' && !useMineRoutes
      ? user?.id
      : mode === 'clinic' && stack === 'user'
        ? (ownerUserId ?? undefined)
        : undefined;

  const load = useCallback(async () => {
    if (!clinicId) return;
    if (mode === 'clinic' && stack === 'user' && !ownerUserId) return;
    if (mode === 'mine' && !useMineRoutes && !user?.id) return;
    setLoading(true);
    setError('');
    try {
      const rows = await listInterpretationRules(
        clinicId,
        kind,
        useMineRoutes ? { mine: true } : { ownerUserId: ownerForAdmin }
      );
      setRules(
        rows.map(toDraft).sort((left, right) => Number(left.isCatchAll) - Number(right.isCatchAll))
      );
      setEditing(null);
    } catch (loadError) {
      setRules([]);
      setError(loadError instanceof Error ? loadError.message : 'Could not load templates');
    } finally {
      setLoading(false);
    }
  }, [clinicId, kind, mode, ownerForAdmin, ownerUserId, stack, useMineRoutes, user?.id]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (mode !== 'clinic' || !clinicId) return;
    void listClinicUsers(clinicId)
      .then(setUsers)
      .catch(() => setUsers([]));
  }, [clinicId, mode]);

  const save = async (rule: RuleDraft, index: number) => {
    const templates: InterpretationTemplateWrite[] = rule.templates
      .map((template) => ({
        name: template.name.trim(),
        body: template.body,
        isDefault: template.isDefault
      }))
      .filter((template) => template.name);
    if (!templates.length) {
      notifications.show({ title: 'Name required', message: 'Add a template name.', color: 'red' });
      return;
    }
    if (!templates.some((template) => template.isDefault)) templates[0].isDefault = true;
    if (
      !rule.isCatchAll &&
      (!rule.deviceTypes.length || !rule.alertTypes.length || !rule.appointmentTypes.length)
    ) {
      notifications.show({
        title: 'Rule needs a match',
        message:
          'Choose a device, alert, and appointment type. The catch-all covers everything else.',
        color: 'red'
      });
      return;
    }
    try {
      if (rule.id) {
        await updateInterpretationRule(clinicId, rule.id, {
          isCatchAll: rule.isCatchAll,
          deviceTypes: rule.deviceTypes,
          alertTypes: rule.alertTypes,
          appointmentTypes: rule.appointmentTypes,
          templates,
          mine: useMineRoutes
        });
      } else {
        await createInterpretationRule(clinicId, {
          kind,
          isCatchAll: rule.isCatchAll,
          deviceTypes: rule.deviceTypes,
          alertTypes: rule.alertTypes,
          appointmentTypes: rule.appointmentTypes,
          templates,
          ownerUserId: useMineRoutes ? undefined : ownerForAdmin,
          mine: useMineRoutes
        });
      }
      notifications.show({ title: 'Saved', message: 'Template rule saved.', color: 'green' });
      setEditing(index);
      await load();
    } catch (saveError) {
      notifications.show({
        title: 'Could not save',
        message: saveError instanceof Error ? saveError.message : 'Save failed',
        color: 'red'
      });
    }
  };

  const archive = async (rule: RuleDraft) => {
    if (!rule.id) {
      setRules((current) => current.filter((item) => item !== rule));
      return;
    }
    try {
      await archiveInterpretationRule(clinicId, rule.id, useMineRoutes);
      await load();
    } catch (archiveError) {
      notifications.show({
        title: 'Could not archive',
        message: archiveError instanceof Error ? archiveError.message : 'Archive failed',
        color: 'red'
      });
    }
  };

  const restore = async (rule: RuleDraft) => {
    if (!rule.id) return;
    try {
      await restoreInterpretationRule(clinicId, rule.id);
      await load();
    } catch (restoreError) {
      notifications.show({
        title: 'Could not restore',
        message: restoreError instanceof Error ? restoreError.message : 'Restore failed',
        color: 'red'
      });
    }
  };

  const move = async (index: number, direction: -1 | 1) => {
    const active = rules.filter((rule) => !rule.isCatchAll && !rule.isArchived && rule.id);
    const currentIndex = active.findIndex((rule) => rule.id === rules[index]?.id);
    const nextIndex = currentIndex + direction;
    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= active.length) return;
    const order = active.map((rule) => rule.id as string);
    const [moved] = order.splice(currentIndex, 1);
    order.splice(nextIndex, 0, moved);
    try {
      await reorderInterpretationRules(clinicId, kind, order, {
        mine: useMineRoutes,
        ownerUserId: useMineRoutes ? undefined : ownerForAdmin
      });
      await load();
    } catch (moveError) {
      notifications.show({
        title: 'Could not reorder',
        message: moveError instanceof Error ? moveError.message : 'Reorder failed',
        color: 'red'
      });
    }
  };

  const userOptions = users.map((clinicUser) => ({
    value: clinicUser.id,
    label:
      [clinicUser.given, clinicUser.family].filter(Boolean).join(' ') ||
      clinicUser.email ||
      clinicUser.id
  }));

  const activeRules = rules.filter((rule) => !rule.isArchived);
  const archivedRules = rules.filter((rule) => rule.isArchived);

  return (
    <Stack gap="md">
      <div>
        <Text fw={700} size="lg">
          {mode === 'mine' ? 'My Templates' : 'Clinic templates'}
        </Text>
        <Text size="sm" c="#81899a" mt={4}>
          {mode === 'mine'
            ? 'Your templates. On a matching report, yours fills the note ahead of the clinic template.'
            : 'Clinic templates apply to everyone at the clinic. Choose a user to edit only their templates.'}
        </Text>
      </div>

      {clinics.length > 1 && (
        <Select
          label="Clinic"
          data={clinics.map((clinic) => ({ value: clinic.id, label: clinic.name }))}
          value={clinicId}
          onChange={(value) => value && setClinicId(value)}
          maw={360}
        />
      )}

      <Group>
        <SegmentedControl
          value={kind}
          onChange={(value) => setKind(value as InterpretationKind)}
          data={[
            { value: 'clinician', label: 'Clinician' },
            { value: 'physician', label: 'Physician' }
          ]}
        />
        {mode === 'clinic' && (
          <SegmentedControl
            value={stack}
            onChange={(value) => setStack(value as 'clinic' | 'user')}
            data={[
              { value: 'clinic', label: 'Clinic' },
              { value: 'user', label: 'A user' }
            ]}
          />
        )}
      </Group>

      {mode === 'clinic' && stack === 'user' && (
        <Select
          label="User"
          placeholder="Choose a user at this clinic"
          data={userOptions}
          value={ownerUserId}
          onChange={setOwnerUserId}
          searchable
          maw={360}
        />
      )}

      {error && (
        <Alert color="red" variant="light">
          {error}
        </Alert>
      )}

      {loading && (
        <Text size="sm" c="#81899a">
          Loading templates…
        </Text>
      )}

      {activeRules.map((rule) => {
        const index = rules.indexOf(rule);
        const open = editing === index;
        return (
          <Paper key={rule.id ?? `new-${index}`} withBorder p="md" radius="md">
            <Group justify="space-between" align="flex-start">
              <div>
                <Text fw={600} c="#1a1a2e">
                  {ruleLabel(rule)}
                </Text>
                <Text size="sm" c="#81899a">
                  {rule.isCatchAll
                    ? 'Used when no earlier rule matches.'
                    : 'Top rules win. This one matches every selected device, alert, and appointment.'}
                </Text>
              </div>
              <Group gap="xs">
                {!rule.isCatchAll && rule.id && (
                  <>
                    <Button size="xs" variant="default" onClick={() => void move(index, -1)}>
                      Up
                    </Button>
                    <Button size="xs" variant="default" onClick={() => void move(index, 1)}>
                      Down
                    </Button>
                  </>
                )}
                <Button
                  size="xs"
                  variant="light"
                  color="plum"
                  onClick={() => setEditing(open ? null : index)}>
                  {open ? 'Close' : 'Edit'}
                </Button>
              </Group>
            </Group>
            {open && (
              <Stack gap="sm" mt="md">
                {!rule.isCatchAll && (
                  <>
                    <MultiSelect
                      label="Device"
                      data={DEVICE_TYPE_OPTIONS}
                      value={rule.deviceTypes}
                      onChange={(deviceTypes) =>
                        setRules((current) =>
                          current.map((item, itemIndex) =>
                            itemIndex === index ? { ...item, deviceTypes } : item
                          )
                        )
                      }
                    />
                    <MultiSelect
                      label="Alert"
                      data={ALERT_TYPE_OPTIONS}
                      value={rule.alertTypes}
                      onChange={(alertTypes) =>
                        setRules((current) =>
                          current.map((item, itemIndex) =>
                            itemIndex === index ? { ...item, alertTypes } : item
                          )
                        )
                      }
                    />
                    <MultiSelect
                      label="Appointment"
                      data={APPOINTMENT_TYPE_OPTIONS}
                      value={rule.appointmentTypes}
                      onChange={(appointmentTypes) =>
                        setRules((current) =>
                          current.map((item, itemIndex) =>
                            itemIndex === index ? { ...item, appointmentTypes } : item
                          )
                        )
                      }
                    />
                  </>
                )}
                {rule.templates.map((template, templateIndex) => (
                  <Paper key={`${rule.id ?? index}-template-${templateIndex}`} withBorder p="sm">
                    <Stack gap="xs">
                      <TextInput
                        label="Template name"
                        value={template.name}
                        onChange={(event) => {
                          const name = event.currentTarget.value;
                          setRules((current) =>
                            current.map((item, itemIndex) =>
                              itemIndex === index
                                ? {
                                    ...item,
                                    templates: item.templates.map((row, rowIndex) =>
                                      rowIndex === templateIndex ? { ...row, name } : row
                                    )
                                  }
                                : item
                            )
                          );
                        }}
                      />
                      <Textarea
                        label="Body"
                        minRows={4}
                        value={template.body}
                        onChange={(event) => {
                          const body = event.currentTarget.value;
                          setRules((current) =>
                            current.map((item, itemIndex) =>
                              itemIndex === index
                                ? {
                                    ...item,
                                    templates: item.templates.map((row, rowIndex) =>
                                      rowIndex === templateIndex ? { ...row, body } : row
                                    )
                                  }
                                : item
                            )
                          );
                        }}
                      />
                      <Button
                        size="xs"
                        variant={template.isDefault ? 'filled' : 'default'}
                        color="plum"
                        onClick={() =>
                          setRules((current) =>
                            current.map((item, itemIndex) =>
                              itemIndex === index
                                ? {
                                    ...item,
                                    templates: withOneDefault(item.templates, templateIndex)
                                  }
                                : item
                            )
                          )
                        }>
                        {template.isDefault ? 'Default' : 'Make default'}
                      </Button>
                    </Stack>
                  </Paper>
                ))}
                <Group>
                  <Button
                    size="xs"
                    variant="default"
                    onClick={() =>
                      setRules((current) =>
                        current.map((item, itemIndex) =>
                          itemIndex === index
                            ? {
                                ...item,
                                templates: [
                                  ...item.templates,
                                  { name: '', body: '', isDefault: false }
                                ]
                              }
                            : item
                        )
                      )
                    }>
                    Add template
                  </Button>
                  <Button size="xs" color="plum" onClick={() => void save(rule, index)}>
                    Save
                  </Button>
                  {!rule.isCatchAll && (
                    <Button
                      size="xs"
                      variant="subtle"
                      color="red"
                      onClick={() => void archive(rule)}>
                      Archive
                    </Button>
                  )}
                </Group>
              </Stack>
            )}
          </Paper>
        );
      })}

      <Button
        variant="light"
        color="plum"
        maw={180}
        onClick={() => {
          const next = blankRule();
          setRules((current) => {
            const catchAll = current.find((rule) => rule.isCatchAll && !rule.isArchived);
            const rest = current.filter((rule) => rule !== catchAll);
            return catchAll ? [...rest, next, catchAll] : [...current, next];
          });
          setEditing(rules.filter((rule) => !rule.isCatchAll && !rule.isArchived).length);
        }}>
        Add rule
      </Button>

      {mode === 'clinic' && archivedRules.length > 0 && (
        <Stack gap="xs">
          <Text size="sm" fw={600} c="#81899a">
            Archived
          </Text>
          {archivedRules.map((rule) => (
            <Group key={rule.id} justify="space-between">
              <Text size="sm" c="#1a1a2e">
                {ruleLabel(rule)}
              </Text>
              <Button size="xs" variant="default" onClick={() => void restore(rule)}>
                Restore
              </Button>
            </Group>
          ))}
        </Stack>
      )}
    </Stack>
  );
};

export default InterpretationTemplatesSection;
